import React, { useCallback } from "react";
import { styled } from "@mui/system";
import _ from "lodash";

import Dropzone, { DropzoneState } from "components/Dropzone";
import toast from "components/Toast";

import { defaultUploadButton } from "./utils";

const Input = styled("input")({
  display: "none",
});
interface IUploadButton {
  label?: string;
  maxFiles?: number;
  accept?: { [key: string]: Array<string> };
  dataTestid?: string;
  onChange: ({ files }: { files: Array<File> }) => void;
  additionalRenderComponentProps?: any;
  renderComponent?: (
    state: Partial<
      DropzoneState & {
        onClick: () => void;
      }
    >
  ) => JSX.Element;
  style?: React.CSSProperties;
}

const UploadButton: React.FC<IUploadButton> = ({
  label = "Browse for Files",
  accept,
  dataTestid,
  maxFiles,
  onChange,
  additionalRenderComponentProps = {},
  renderComponent = defaultUploadButton,
  style = {},
}) => {
  const handleRejected = useCallback(
    (files: any) => {
      files.map(
        ({
          errors,
          file,
        }: {
          errors: Array<{ code: string; message: string }>;
          file: File;
        }) => {
          let errorMessage = errors[0].message;

          if (errors[0].code === "too-many-files") {
            errorMessage = `${errorMessage}. You can upload a maximum of ${maxFiles} files at once.`;
          } else if (
            errors[0].code === "file-invalid-type" &&
            !_.isNil(accept)
          ) {
            errorMessage = `Invalid file type. Please upload a file with one of the following extensions: ${_.uniq(
              Object.values(accept).flatMap((item) => item)
            ).join(", ")}`;
          }

          return toast.errorMessage(`${file.name} - ${errorMessage}`);
        }
      );
    },
    [maxFiles, accept]
  );

  const handleAccepted = useCallback(
    (files: Array<File>) => {
      onChange({ files });
    },
    [onChange]
  );

  return (
    <Dropzone
      accept={accept}
      onDropRejected={handleRejected}
      onDropAccepted={handleAccepted}
      maxFiles={maxFiles}
    >
      {({ getRootProps, getInputProps, isDragActive }: DropzoneState) => (
        <div
          {...getRootProps()}
          style={{ width: "100%", ...style }}
          data-testid="upload-file-dropzone"
        >
          <Input {...getInputProps()} type="file" data-testid={dataTestid} />
          {renderComponent({
            state: { getRootProps, getInputProps, isDragActive },
            label,
            additionalProps: additionalRenderComponentProps,
          })}
        </div>
      )}
    </Dropzone>
  );
};

export default UploadButton;
